import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

interface GoogleLoginButtonProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

declare global {
  interface Window {
    google: any;
    googleLoginCallback: (response: any) => void;
  }
}

const GoogleLoginButton = ({ onSuccess, onError }: GoogleLoginButtonProps) => {
  const { login } = useAuth();
  const buttonRef = useRef<HTMLDivElement>(null);

  // Define the callback function globally BEFORE useEffect
  const handleGoogleLogin = async (response: any) => {
    try {
      console.log('Google login response received:', response);
      await login(response.credential);
      toast.success('Successfully logged in!');
      onSuccess?.();
    } catch (error) {
      console.error('Login failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Login failed. Please try again.';
      toast.error(errorMessage);
      onError?.(errorMessage);
    }
  };

  useEffect(() => {
    // Set the callback function globally
    window.googleLoginCallback = handleGoogleLogin;

    // Load Google Identity Services script
    const loadGoogleScript = () => {
      if (window.google) {
        initializeGoogleSignIn();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogleSignIn;
      script.onerror = () => {
        console.error('Failed to load Google Identity Services script');
        toast.error('Failed to load Google authentication. Please refresh the page.');
      };
      document.head.appendChild(script);
    };

    const initializeGoogleSignIn = () => {
      const clientId = (import.meta as any).env?.VITE_GOOGLE_CLIENT_ID;
      
      if (!clientId) {
        console.error('VITE_GOOGLE_CLIENT_ID is not configured');
        toast.error('Google authentication is not properly configured');
        return;
      }

      console.log('Initializing Google Sign-In with client ID:', clientId);
      console.log('Current origin:', window.location.origin);
      console.log('Callback function available:', typeof window.googleLoginCallback);

      if (window.google && buttonRef.current) {
        try {
          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: window.googleLoginCallback,
            auto_select: false,
            cancel_on_tap_outside: true,
          });

          window.google.accounts.id.renderButton(buttonRef.current, {
            theme: 'outline',
            size: 'large',
            text: 'signin_with',
            shape: 'rectangular',
            logo_alignment: 'left',
            width: 280,
          });

          console.log('Google Sign-In button rendered successfully');
        } catch (error) {
          console.error('Error initializing Google Sign-In:', error);
          toast.error('Failed to initialize Google authentication');
        }
      }
    };

    loadGoogleScript();

    // Cleanup
    return () => {
      if ('googleLoginCallback' in window) {
        delete (window as any).googleLoginCallback;
      }
    };
  }, [login, onSuccess, onError, handleGoogleLogin]);

  return (
    <div className="flex flex-col items-center space-y-4">
      <div ref={buttonRef} className="google-signin-button"></div>
      <p className="text-sm text-gray-600 text-center max-w-xs">
        Sign in with your Google account to access ProductTrace features
      </p>
      <div className="text-xs text-gray-500 text-center">
        <p>Current origin: {window.location.origin}</p>
        <p>Client ID: {(import.meta as any).env?.VITE_GOOGLE_CLIENT_ID ? 'Configured' : 'Missing'}</p>
      </div>
    </div>
  );
};

export default GoogleLoginButton; 